import { userService } from "../user.service";
import { trackExistingCustomerLogin } from "../../../_services/metaTracking";
import { appstoreSetupReducerActions, authenticationReducerActions } from "../../../_services/store";
import { selectOrganizationByLogin } from "./authentication.actions";
import {
    fireGTMTagAfterSuccessfulUserRegistration,
    fireMetaTagAfterSuccessfulUserRegistration,
} from "../../../organization/myOrganization/services/store/myOrganization.actions";
import {
    GoogleAuthProvider,
    OAuthProvider,
    signInWithPopup,
    getMultiFactorResolver,
    TotpMultiFactorGenerator,
} from "firebase/auth";
import { auth } from "../../../firebase";

// Firebase MultiFactorResolver instances aren't serializable into Redux, so the
// pending resolver is held in module scope between the sign-in attempt that raised
// auth/multi-factor-auth-required and the code submission that resolves it. Kept
// local to this flow (rather than a shared window global) so it can neither clobber
// nor be clobbered by the main login flow's MFA resolver.
let pendingMfaResolver = null;

// An account can enrol several MFA factors; select the TOTP factor explicitly
// instead of assuming hints[0], which could pick a non-TOTP factor and reject a
// valid code.
const getTotpHint = (resolver) => resolver?.hints?.find((hint) => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID);

// Resolves a pending TOTP challenge with the entered code. Failures here are the
// bad/expired-code case (callers map them via mapMfaError and keep the code step);
// it is kept separate from the post-sign-in work so those failures aren't
// misreported as MFA errors.
const resolveTotpSignIn = async (resolver, verificationCode) => {
    const totpHint = getTotpHint(resolver);
    if (!totpHint) {
        throw new Error("no-resolver");
    }
    const assertion = TotpMultiFactorGenerator.assertionForSignIn(totpHint.uid, verificationCode);
    return resolver.resolveSignIn(assertion);
};

// Runs the Firebase SSO popup for the given provider and returns a token object
// in the same shape processOAuthToken/mergeUserIntoStorage expect.
const signInWithSSOProvider = async (providerId) => {
    auth.tenantId = process.env.REACT_APP_AUTH_TENANT_ID;

    let provider;
    if (providerId === "google") {
        provider = new GoogleAuthProvider();
    } else if (providerId === "microsoft") {
        provider = new OAuthProvider("microsoft.com");
        // Match microsoftLogin (authentication.actions.js): without these scopes the
        // email claim can be absent, leaving user.mail empty on first-time link.
        provider.addScope("openid");
        provider.addScope("profile");
        provider.addScope("email");
    } else {
        throw new Error("Unsupported SSO provider: " + providerId);
    }

    const result = await signInWithPopup(auth, provider);
    const firebaseIdToken = await result.user.getIdToken();
    const tokenResult = await result.user.getIdTokenResult();

    return {
        access_token: firebaseIdToken,
        expiration_time: tokenResult.expirationTime,
        refresh_token: result.user.refreshToken,
    };
};

// auth/multi-factor-auth-required is handled separately (TOTP-code step); the
// remaining auth states the appstore login screen can't drive get mapped to a
// clear message instead of leaving the user stuck. Covers both SSO-popup and
// email/password sign-in codes.
const mapAuthError = (error) => {
    switch (error?.code) {
        case "auth/account-exists-with-different-credential":
            return "Für diese E-Mail-Adresse existiert bereits ein Konto mit einer anderen Anmeldemethode. Bitte melde dich mit deiner bisherigen Methode an.";
        case "auth/popup-closed-by-user":
        case "auth/cancelled-popup-request":
            return "Die Anmeldung wurde abgebrochen. Bitte versuche es erneut.";
        case "auth/wrong-password":
        case "auth/invalid-credential":
            return "Das eingegebene Passwort ist falsch. Falls dein Konto über Google oder Microsoft erstellt wurde, verwende bitte den entsprechenden Login-Button.";
        case "auth/user-not-found":
            return "Kein Benutzer mit dieser E-Mail-Adresse gefunden.";
        case "auth/invalid-email":
            return "Die E-Mail-Adresse ist ungültig.";
        case "auth/too-many-requests":
            return "Zu viele fehlgeschlagene Anmeldeversuche. Bitte versuche es später erneut.";
        case "auth/user-disabled":
            return "Dieser Benutzeraccount wurde deaktiviert.";
        default:
            return "Bei der Anmeldung ist ein Fehler aufgetreten. Bitte versuche es erneut.";
    }
};

// Mirrors resolveMFA (authentication.actions.js): messages for the TOTP-code step.
const mapMfaError = (error) => {
    switch (error?.code) {
        case "auth/invalid-verification-code":
            return "Der eingegebene Bestätigungscode ist ungültig. Bitte versuche es erneut.";
        case "auth/code-expired":
            return "Der Bestätigungscode ist abgelaufen. Bitte melde dich erneut an.";
        default:
            return "Fehler bei der 2-Faktor-Authentifizierung. Bitte versuche es erneut.";
    }
};

export const submitNewOrganizationAndUserRegistrationIncludingExistingAuthData = (userData) => {
    return (dispatch) => {
        return new Promise(async (resolve, reject) => {
            dispatch(appstoreSetupReducerActions.submitAppstoreNewOrgOrUserRequest());
            try {
                const newOrg = await userService.registerWithAuthData(userData);
                fireGTMTagAfterSuccessfulUserRegistration();
                fireMetaTagAfterSuccessfulUserRegistration();
                resolve(newOrg);
            } catch (error) {
                dispatch(appstoreSetupReducerActions.submitAppstoreNewOrgOrUserFailure(error));
                reject(error);
            }
        });
    };
};

export const submitNewUserToExistingShopifyOrg = (userData) => {
    return (dispatch) => {
        return new Promise(async (resolve, reject) => {
            dispatch(appstoreSetupReducerActions.submitAppstoreNewOrgOrUserRequest());
            try {
                const newOrg = await userService.registerNewAppstoreUser(userData);
                fireGTMTagAfterSuccessfulUserRegistration();
                fireMetaTagAfterSuccessfulUserRegistration();

                resolve(newOrg);
            } catch (error) {
                dispatch(appstoreSetupReducerActions.submitAppstoreNewOrgOrUserFailure(error));
                reject(error);
            }
        });
    };
};

// authDataExisted=true means the shop's connectedAuthData already exists and is
// bound to organizationExternalUUID; connecting an existing user is therefore just
// "select that org, then attach the user to the nounce" — no org selection or
// create-shop step (unlike the authDataExisted=false flow). Redirect is left to the
// caller (screen navigates), so no redirectUrl is passed to selectOrganizationByLogin.
const associateExistingStore = async (dispatch, user, existingUserData) => {
    const requestDto = {
        email: user?.mail || user?.email || existingUserData.email,
        organizationExternalUUID: existingUserData.organizationExternalUUID,
        connectedAuthDataExternalUUID: existingUserData.connectedAuthDataExternalUUID,
        mainContactExternalUUID: user.externalUUID,
    };
    // Select the org first: it force-refreshes the Firebase token with the org claim,
    // persists it, and dispatches loginSuccess with the org-selected user. The nounce
    // endpoint rejects the request ("Der Benutzer hat keine Organisation ausgewählt")
    // unless an organization is already selected, so it must run after this.
    const selectedUser = await dispatch(selectOrganizationByLogin(existingUserData.organizationExternalUUID, null));
    if (!selectedUser || !selectedUser.selectedOrganization) {
        throw new Error("organization-selection-failed");
    }
    await userService.updateShopifyNounceUser(existingUserData.organizationName, requestDto);
    // Return the org-selected user so callers use it rather than re-dispatching the
    // original (org-less) login user over the top.
    return selectedUser;
};

export const logExistingUserIntoExistingStore = (existingUserData) => {
    return async (dispatch) => {
        dispatch(appstoreSetupReducerActions.logExistingUserIntoExistingStoreRequest());
        try {
            const { email, password } = existingUserData;
            const user = await userService.login(email, password, null);
            const selectedUser = await associateExistingStore(dispatch, user, existingUserData);

            dispatch(appstoreSetupReducerActions.logExistingUserIntoExistingStoreSuccess());
            return selectedUser;
        } catch (error) {
            if (error?.code === "auth/multi-factor-auth-required") {
                // Same TOTP handoff as the SSO path: stash the resolver and switch the
                // screen to the code step; resolveLogExistingUserIntoExistingStoreMfa finishes.
                pendingMfaResolver = getMultiFactorResolver(auth, error);
                dispatch(appstoreSetupReducerActions.existingUserSSOMfaRequired());
                return null;
            }
            dispatch(appstoreSetupReducerActions.logExistingUserIntoExistingStoreFailure(mapAuthError(error)));
            throw error;
        }
    };
};

// SSO equivalent of logExistingUserIntoExistingStore.
export const logExistingUserIntoExistingStoreViaSSO = (providerId, existingUserData) => {
    return async (dispatch) => {
        dispatch(appstoreSetupReducerActions.logExistingUserIntoExistingStoreRequest());
        try {
            const tokenUser = await signInWithSSOProvider(providerId);
            const user = await userService.processOAuthToken(tokenUser);
            const selectedUser = await associateExistingStore(dispatch, user, existingUserData);

            dispatch(appstoreSetupReducerActions.logExistingUserIntoExistingStoreSuccess());
            return selectedUser;
        } catch (error) {
            if (error?.code === "auth/multi-factor-auth-required") {
                pendingMfaResolver = getMultiFactorResolver(auth, error);
                dispatch(appstoreSetupReducerActions.existingUserSSOMfaRequired());
                return null;
            }
            dispatch(appstoreSetupReducerActions.logExistingUserIntoExistingStoreFailure(mapAuthError(error)));
            throw error;
        }
    };
};

// Completes either existing-store login path after a TOTP challenge: the resolver
// was stashed by whichever path hit MFA, so resolution here is identical.
export const resolveLogExistingUserIntoExistingStoreMfa = (verificationCode, existingUserData) => {
    return async (dispatch) => {
        dispatch(appstoreSetupReducerActions.logExistingUserIntoExistingStoreRequest());

        let result;
        try {
            result = await resolveTotpSignIn(pendingMfaResolver, verificationCode);
        } catch (error) {
            // Bad/expired code: keep existingUserMfaRequired and the resolver so the user can retry.
            dispatch(appstoreSetupReducerActions.logExistingUserIntoExistingStoreFailure(mapMfaError(error)));
            return null;
        }

        try {
            const firebaseIdToken = await result.user.getIdToken();
            const tokenResult = await result.user.getIdTokenResult();
            const user = await userService.processOAuthToken({
                access_token: firebaseIdToken,
                expiration_time: tokenResult.expirationTime,
                refresh_token: result.user.refreshToken,
            });

            pendingMfaResolver = null;
            dispatch(appstoreSetupReducerActions.existingUserSSOMfaResolved());
            const selectedUser = await associateExistingStore(dispatch, user, existingUserData);

            dispatch(appstoreSetupReducerActions.logExistingUserIntoExistingStoreSuccess());
            return selectedUser;
        } catch (error) {
            // MFA already passed — clear it and report a generic login error, not an MFA error.
            pendingMfaResolver = null;
            dispatch(appstoreSetupReducerActions.existingUserSSOMfaResolved());
            dispatch(appstoreSetupReducerActions.logExistingUserIntoExistingStoreFailure(mapAuthError(error)));
            return null;
        }
    };
};

export const connectNewShopToExistingUserInitialLogin = (existingUserData) => {
    return async (dispatch) => {
        dispatch(appstoreSetupReducerActions.connectNewShopToExistingUserInitialLoginRequest());
        try {
            const { email, password, organizationExternalUUID } = existingUserData;

            dispatch(authenticationReducerActions.loginRequest({ mail: email }));
            const user = await userService.login(email, password, organizationExternalUUID);

            dispatch(appstoreSetupReducerActions.connectNewShopToExistingUserInitialLoginSuccess(user));
            dispatch(authenticationReducerActions.loginSuccess(user));
            trackExistingCustomerLogin(user);
            return user;
        } catch (error) {
            dispatch(appstoreSetupReducerActions.connectNewShopToExistingUserInitialLoginFailure(error));
            dispatch(authenticationReducerActions.loginFailure(error));

            throw error;
        }
    };
};

// SSO equivalent of connectNewShopToExistingUserInitialLogin: authenticates via
// a Firebase popup instead of email/password, then lands in the exact same state
// (existingUserAvailableOrgs populated) so phase 2 (org selection + create-shop)
// is reused unchanged.
export const connectNewShopToExistingUserInitialLoginViaSSO = (providerId) => {
    return async (dispatch) => {
        dispatch(appstoreSetupReducerActions.connectNewShopToExistingUserInitialLoginRequest());
        try {
            const tokenUser = await signInWithSSOProvider(providerId);
            const user = await userService.processOAuthToken(tokenUser);

            dispatch(appstoreSetupReducerActions.connectNewShopToExistingUserInitialLoginSuccess(user));
            dispatch(authenticationReducerActions.loginSuccess(user));
            trackExistingCustomerLogin(user);
            return user;
        } catch (error) {
            if (error?.code === "auth/multi-factor-auth-required") {
                // TOTP-protected account: stash the resolver (same window global the
                // main login flow uses) and flip the screen to the code-input step.
                // resolveConnectNewShopToExistingUserSSOMfa finishes the sign-in.
                pendingMfaResolver = getMultiFactorResolver(auth, error);
                dispatch(appstoreSetupReducerActions.existingUserSSOMfaRequired());
                return null;
            }
            dispatch(appstoreSetupReducerActions.connectNewShopToExistingUserInitialLoginFailure(mapAuthError(error)));
            throw error;
        }
    };
};

// Second half of the SSO login when the account has TOTP MFA: takes the 6-digit
// code, resolves the pending Firebase sign-in, then lands in the exact same state
// as the non-MFA SSO path (existingUserAvailableOrgs populated) so phase 2 is
// reused unchanged. Returns null on failure so the screen stays on the code step.
export const resolveConnectNewShopToExistingUserSSOMfa = (verificationCode) => {
    return async (dispatch) => {
        dispatch(appstoreSetupReducerActions.connectNewShopToExistingUserInitialLoginRequest());

        let result;
        try {
            result = await resolveTotpSignIn(pendingMfaResolver, verificationCode);
        } catch (error) {
            // Bad/expired code: keep existingUserMfaRequired and the resolver so the user can retry.
            dispatch(appstoreSetupReducerActions.connectNewShopToExistingUserInitialLoginFailure(mapMfaError(error)));
            return null;
        }

        try {
            const firebaseIdToken = await result.user.getIdToken();
            const tokenResult = await result.user.getIdTokenResult();
            const tokenUser = {
                access_token: firebaseIdToken,
                expiration_time: tokenResult.expirationTime,
                refresh_token: result.user.refreshToken,
            };
            const user = await userService.processOAuthToken(tokenUser);

            pendingMfaResolver = null;
            dispatch(appstoreSetupReducerActions.existingUserSSOMfaResolved());
            dispatch(appstoreSetupReducerActions.connectNewShopToExistingUserInitialLoginSuccess(user));
            dispatch(authenticationReducerActions.loginSuccess(user));
            trackExistingCustomerLogin(user);
            return user;
        } catch (error) {
            // MFA already passed — clear it and report a generic login error, not an MFA error.
            pendingMfaResolver = null;
            dispatch(appstoreSetupReducerActions.existingUserSSOMfaResolved());
            dispatch(appstoreSetupReducerActions.connectNewShopToExistingUserInitialLoginFailure(mapAuthError(error)));
            return null;
        }
    };
};

// Abandons a pending TOTP challenge: drops the stashed resolver and returns the
// login screen to the email/password + SSO step.
export const cancelConnectNewShopToExistingUserSSOMfa = () => {
    return (dispatch) => {
        pendingMfaResolver = null;
        dispatch(appstoreSetupReducerActions.existingUserSSOMfaReset());
    };
};

export const connectNewShopToExistingOrgAndUser = (existingUserData) => {
    return (dispatch) => {
        return new Promise(async (resolve, reject) => {
            dispatch(appstoreSetupReducerActions.submitAppstoreNewOrgOrUserRequest());
            try {
                let user = await userService.selectOrganization(
                    existingUserData.selectedOrganizationExternalUUID,
                    existingUserData.emailVerificationCode
                );
                user = await userService.mergeUserIntoStorage(user);
                dispatch(authenticationReducerActions.loginSuccess(user));
                trackExistingCustomerLogin(user);
                const updatedOrg = await userService.createNewShopForExistingOrgAndUser(existingUserData);
                resolve(updatedOrg);
            } catch (error) {
                dispatch(appstoreSetupReducerActions.submitAppstoreNewOrgOrUserFailure(error));
                reject(error);
            }
        });
    };
};

export const createNewOrgForExistingUser = (registrationData) => {
    return (dispatch) => {
        return new Promise(async (resolve, reject) => {
            dispatch(appstoreSetupReducerActions.submitAppstoreNewOrgOrUserRequest());
            try {
                const newOrg = await userService.createNewAppstoreOrgForExistingUser(registrationData);
                resolve(newOrg);
            } catch (error) {
                dispatch(appstoreSetupReducerActions.submitAppstoreNewOrgOrUserFailure(error));
                reject(error);
            }
        });
    };
};
